import React from "react";
import {useTranslation} from "react-i18next";
import {useParams, Link} from "react-router-dom";
import MatchBox from "../components/matches/MatchBox";
import {formatDate, playerToString} from "../util/StringUtils";
import ReactCountryFlag from "react-country-flag";
import {useMatchDetail} from "../hooks/queries/useMatch";

function MatchDetail() {
    const { id } = useParams()
    const { t } = useTranslation();
    const { data: matchDetail } = useMatchDetail(id);

    const getCurrentMatch = () => {
        return (
            <div className="mb-4">
                <MatchBox variant="profile" match={matchDetail.match} hideStatus={true} showDetailButton={false} />
            </div>
        );
    }

    const getComparisonRow = (label: string, p1Value: any, p2Value: any, options: any = {}) => {
        const { highlightBetter = false, higherIsBetter = true, isPercentage = false, isForm = false } = options;

        let p1Class = "";
        let p2Class = "";

        if (highlightBetter && p1Value !== p2Value && p1Value != null && p2Value != null) {
            const p1Better = higherIsBetter ? p1Value > p2Value : p1Value < p2Value;
            p1Class = p1Better ? "win" : "loss";
            p2Class = p1Better ? "loss" : "win";
        }

        const formatValue = (val: any) => {
            if (val == null || val === "") return "-";
            if (isPercentage) return `${(val * 100).toFixed(0)}%`;
            if (typeof val === "number" && !Number.isInteger(val)) return val.toFixed(2);
            return val;
        };

        const renderFormString = (form: string | null | undefined) => {
            if (!form || form === "") return <span className="text-muted">-</span>;
            return (
                <span>
                    {form.split("").map((char: string, idx: number) => (
                        <span key={idx} className={char === "W" ? "win" : "loss"} style={{fontWeight: "bold"}}>
                            {char}&nbsp;
                        </span>
                    ))}
                </span>
            );
        };

        return (
            <div className="comparison-row d-flex align-items-center py-2" style={{borderBottom: "1px solid #444"}}>
                <div className={`text-end flex-grow-1 ${p1Class}`} style={{minWidth: "80px"}}>
                    {isForm ? renderFormString(p1Value) : formatValue(p1Value)}
                </div>
                <div className="text-center px-3" style={{minWidth: "140px", color: "#aaa", fontSize: "0.9em"}}>
                    {label}
                </div>
                <div className={`text-start flex-grow-1 ${p2Class}`} style={{minWidth: "80px"}}>
                    {isForm ? renderFormString(p2Value) : formatValue(p2Value)}
                </div>
            </div>
        );
    };

    const getVersusHeader = () => {
        const p1 = matchDetail.match.playerOne;
        const p2 = matchDetail.match.playerTwo;

        return (
            <div className="versus-header d-flex align-items-center py-3 mb-3" style={{borderBottom: "2px solid #666"}}>
                <div className="text-center flex-grow-1">
                    <Link to={"/players/" + p1.id} style={{ textDecoration: 'none', fontSize: "1em" }}>
                        <ReactCountryFlag countryCode={p1.nationality ?? "UN"} svg title={p1.nationality ?? "UN"} className="flag me-2" />
                        {playerToString(p1, false, true)}
                    </Link>
                </div>
                <div className="text-center px-3" style={{fontSize: "1.2em", fontWeight: "bold", color: "#888"}}>
                    VS
                </div>
                <div className="text-center flex-grow-1">
                    <Link to={"/players/" + p2.id} style={{ textDecoration: 'none', fontSize: "1em" }}>
                        <ReactCountryFlag countryCode={p2.nationality ?? "UN"} svg title={p2.nationality ?? "UN"} className="flag me-2" />
                        {playerToString(p2, false, true)}
                    </Link>
                </div>
            </div>
        );
    };

    const getComparisonStats = () => {
        const stats = matchDetail.comparisonStats;
        const hasH2H = stats.playerOneH2HWins > 0 || stats.playerTwoH2HWins > 0;

        return (
            <div className="comparison-stats mb-4">
                {getVersusHeader()}

                {/* Win Probability */}
                {getComparisonRow(t("winProbability"), stats.playerOneWinProbability, stats.playerTwoWinProbability,
                    { highlightBetter: true, higherIsBetter: true, isPercentage: true })}

                {/* ELO at match time */}
                {getComparisonRow(t("elo"), stats.playerOneElo, stats.playerTwoElo,
                    { highlightBetter: true, higherIsBetter: true })}

                {/* Current Form */}
                {getComparisonRow(t("currentForm"), stats.playerOneForm, stats.playerTwoForm, { isForm: true })}

                {/* Total Matches (Experience) */}
                {getComparisonRow(t("matchesPlayed"), stats.playerOneTotalMatches, stats.playerTwoTotalMatches,
                    { highlightBetter: true, higherIsBetter: true })}

                {/* Average Rating */}
                {getComparisonRow(t("avgRating"), stats.playerOneAvgRating, stats.playerTwoAvgRating,
                    { highlightBetter: true, higherIsBetter: true })}

                {/* H2H Section Header */}
                <div className="mt-4 mb-2 text-center" style={{color: "#888", fontSize: "0.9em", textTransform: "uppercase"}}>
                    {t("headToHead")}
                </div>

                {hasH2H ? (
                    <>
                        {/* H2H Wins */}
                        {getComparisonRow(t("matchesWon"), stats.playerOneH2HWins, stats.playerTwoH2HWins,
                            { highlightBetter: true, higherIsBetter: true })}

                        {/* H2H Sets Won */}
                        {getComparisonRow(t("setsWon"), stats.playerOneH2HSetsWon, stats.playerTwoH2HSetsWon,
                            { highlightBetter: true, higherIsBetter: true })}

                        {/* H2H Points Won */}
                        {getComparisonRow(t("pointsWon"), stats.playerOneH2HPointsWon, stats.playerTwoH2HPointsWon,
                            { highlightBetter: true, higherIsBetter: true })}

                        {/* H2H Elo Gained */}
                        {getComparisonRow(t("eloGained"), stats.playerOneH2HEloGained, stats.playerTwoH2HEloGained,
                        { highlightBetter: true, higherIsBetter: true })}

                        {/* Last Victory */}
                        {getComparisonRow(t("lastVictory"),
                            stats.playerOneLastVictoryDate ? formatDate(stats.playerOneLastVictoryDate) : null,
                            stats.playerTwoLastVictoryDate ? formatDate(stats.playerTwoLastVictoryDate) : null
                        )}
                    </>
                ) : (
                    <div className="text-center py-3 text-muted">
                        {t("noHeadToHeadHistory")}
                    </div>
                )}
            </div>
        );
    };

    const getH2HMatchHistory = () => {
        if (matchDetail.headToHeadMatches.length === 0) return null;

        return (
            <div className="h2h-history mt-4">
                <h5 className="mb-3">{t("previousMatches")}</h5>
                {matchDetail.headToHeadMatches.map(match => (
                    <MatchBox variant="profile" key={"h2h-match-" + match.id} match={match} showDetailButton={false} hideStatus={true} />
                ))}
            </div>
        );
    };

    return !matchDetail ? <div/> : (
        <div className="base-container">
            <div className="table-header header">
                <span>{t("matchDetail")}</span>
            </div>
            <div className="content">
                {getCurrentMatch()}
                {getComparisonStats()}
                {getH2HMatchHistory()}
            </div>
        </div>
    );
}

export default MatchDetail;
